import { ChoiceLabels } from './questionParameters';

type Choice<Id extends string | number> = {
  id: Id;
  name: string;
};

type ConfiguredOption<Id extends string | number> = Id | {
  id: Id;
  label?: string;
};

const toChoice = <Id extends string | number>(
  choices: Choice<Id>[],
  option: ConfiguredOption<Id>,
): Choice<Id> | undefined => {
  const id = typeof option === 'object' ? option.id : option;
  const builtInChoice = choices.find((choice) => choice.id === id);

  if (typeof option !== 'object') return builtInChoice;

  const name = option.label || builtInChoice?.name;

  return name === undefined ? undefined : { ...builtInChoice, id, name };
};

const labelChoices = <Id extends string | number>(
  choices: Choice<Id>[],
  choiceLabels?: ChoiceLabels,
): Choice<Id>[] => choices.map((choice) => ({
  ...choice,
  name: choiceLabels?.[String(choice.id)] ?? choice.name,
}));

const applyChoices = <Id extends string | number>(
  choices: Choice<Id>[],
  options?: ConfiguredOption<Id>[],
  choiceLabels?: ChoiceLabels,
): Choice<Id>[] => {
  const configuredChoices = options
    ? options.flatMap((option) => toChoice(choices, option) ?? [])
    : choices;
  const configuredChoiceIds = configuredChoices.map((choice) => choice.id);
  const removedChoices = choices.filter((choice) => !configuredChoiceIds.includes(choice.id));

  return labelChoices([...configuredChoices, ...removedChoices], choiceLabels);
};

export default applyChoices;
